// src/pages/orders/EditOrderPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  ShoppingCart,
  X,
  Store,
  User,
  Package,
  CreditCard,
  FileText,
  CheckCircle,
  Loader2,
  Plus
} from 'lucide-react';
import { getEcomOrder, updateEcomOrder } from '../../services/api/orders';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { usePermissions } from '../../hooks/usePermissions';
import useOrderForm from '../../hooks/useOrderForm';
import ShopSelectionSection from './components/ShopSelectionSection';
import CustomerInformationSection from './components/CustomerInformationSection';
import OrderItemsSection from './components/OrderItemsSection';
import OrderStatusSection from './components/OrderStatusSection';
import PaymentRecordsSection from './components/PaymentRecordsSection';
import OrderSummarySection from './components/OrderSummarySection';
import OrderOriginSection from './components/OrderOriginSection';
import ShiftPickerSection from './components/ShiftPickerSection';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import styles from './CreateOrderPage.module.css';

const EditOrderPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  const { settings: crossSellingSettings } = useBusinessSettings('crossSelling');
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');

  const {
    formData,
    requiredFields,
    referenceData,
    selectableOrderStatuses,
    userShops,
    selectedShop,
    isLoading: isLoadingReference,
    isCalculatingDelivery,
    errors,
    orderTotals,
    replacedOrderData,
    isLookingUpOrder,
    orderLookupError,
    canChangeUnitPrice,
    canGiveDiscount,
    canChangeDeliveryCharge,
    handleShopChange,
    handleFieldChange,
    handlePhoneNumberChange,
    handlePhoneValidationChange,
    handleWeightChange,
    togglePrimaryPhone,
    addPhoneNumber,
    removePhoneNumber,
    addOrderItem,
    updateOrderItem,
    removeOrderItem,
    updateBundleQuantity,
    removeBundle,
    setBundleCrossSell,
    updateReturnItemQty,
    addPaymentRecord,
    updatePaymentRecord,
    removePaymentRecord,
    validateForm,
    getOrderNumberPreview,
    requiresPaymentRecords,
    allowsOptionalPaymentRecords,
    isReplacementOrder,
    getStatusReasons,
    setReplacedOrderData,
    setFormData,
    setErrors
  } = useOrderForm(null, { disableDeliveryChargeAutoCalc: true, currentOrderId: orderId ? parseInt(orderId) : null });

  useEffect(() => {
    const loadOrderData = async () => {
      try {
        setIsLoadingOrder(true);
        const response = await getEcomOrder(orderId);
        const order = response.order;
        
        const userPermissions = user?.role?.permissions || [];
        
        if (order.OrderStatus.isDispatched && !userPermissions.includes('edit_dispatched_order')) {
          error(t('ecomOrders.noPermissionEditDispatchedOrders'), { title: t('common.error'), duration: 5000 });
          navigate('/orders');
          return;
        }
        
        if (order.OrderStatus.isInPacking && !userPermissions.includes('edit_in_packing_order')) {
          error(t('ecomOrders.noPermissionEditInPackingOrders'), { title: t('common.error'), duration: 5000 });
          navigate('/orders');
          return;
        }
        
        setOrderData(order);
        
        let replacedOrderLookupValue = '';
        if (order.replacementDetail?.replacedOrderId && order.replacementDetail?.replacedOrder) {
          replacedOrderLookupValue = order.replacementDetail.replacedOrder.orderNumber || '';
          setReplacedOrderData(order.replacementDetail.replacedOrder);
        }
        const returnItems = (order.replacementDetail?.returnItems || []).map(item => ({
          replacedOrderItemId: item.replacedOrderItemId,
          productName: item.replacedOrderItem?.Product?.name,
          variationSku: item.replacedOrderItem?.ProductVariation?.sku,
          originalQuantity: parseFloat(item.replacedOrderItem?.quantity || 0),
          remainingQty: parseFloat(item.returnQuantity),
          returnQty: parseFloat(item.returnQuantity),
        }));

        const transformedData = {
          shopId: order.Shop.id,
          customerName: order.customerName || '',
          privateReference: order.privateReference || '',
          address: order.address || '',
          cityId: order.City.id,
          phoneNumbers: order.phoneNumbers?.length > 0 
            ? order.phoneNumbers.map(p => ({ phoneNumber: p.phoneNumber, isPrimary: p.isPrimary }))
            : [{ phoneNumber: '', isPrimary: true }],
          customerEmail: order.customerEmail || '',
          deliveryNotes: order.deliveryNotes || '',
          specialNotes: order.specialNotes || '',
          orderStatusKey: order.OrderStatus.key,
          orderStatusReasonKey: order.OrderStatusReason?.key || null,
          holdUntilDate: order.holdUntilDate || null,
          paymentTypeKey: order.PaymentType.key,
          replacedOrderLookupValue: replacedOrderLookupValue,
          replacedOrderId: order.replacementDetail?.replacedOrderId || null,
          replacementCodAmount: order.codAmount || order.grandTotal,
          replacementReasonKey: order.replacementDetail?.ReplacementReason?.key || null,
          returnAirwaybillNumber: order.replacementDetail?.returnAirwaybillNumber || '',
          returnItems,
          orderItems: order.orderItems?.map(item => ({
            id: item.id,
            productId: item.productId,
            variationId: item.variationId,
            displayName: item.Product.name + (item.ProductVariation ? ` - ${item.ProductVariation.attributeDisplay}` : ''),
            sku: item.ProductVariation?.sku || item.Product.sku,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            originalUnitPrice: item.unitPrice,
            manuallyPriced: false,
            lineDiscount: item.lineDiscount || 0,
            manuallyDiscounted: false,
            weight: item.ProductVariation?.weight ?? item.Product?.weight ?? null,
            offers: item.offers || [],
            isCrossSell: !!item.crossSellInfo,
            crossSellQuantity: item.crossSellInfo?.crossSellQuantity != null ? Number(item.crossSellInfo.crossSellQuantity) : null,
            crossSellById: item.crossSellInfo?.crossSellBy ?? null,
            baselineQuantity: item.crossSellInfo?.baselineQuantity != null ? Number(item.crossSellInfo.baselineQuantity) : null,
            originalQuantity: item.quantity,
            ...(item.bundleGroupKey && {
              bundleProductId: item.bundleProductId,
              bundleGroupKey: item.bundleGroupKey,
              bundleQuantity: parseFloat(item.bundleQuantity) || 1,
              bundleName: item.bundleProduct?.name || null,
              bundleSku: item.bundleProduct?.sku || null,
              componentQuantity: Number(item.quantity) / (parseFloat(item.bundleQuantity) || 1),
              componentLineDiscount: Number(item.lineDiscount || 0) / (parseFloat(item.bundleQuantity) || 1),
            }),
          })) || [],
          deliveryCharge: order.deliveryCharge || 0,
          weight: parseFloat(order.weight) || 1,
          orderOriginKey: order.OrderOrigin?.key || null,
          messageCenterShiftId: order.shiftMap?.shiftId || null,
          messageCenterShiftDate: order.shiftMap?.shift?.shiftDate || null,
          createdByUserId: order.createdBy?.id ?? order.createdByUserId ?? null,
          paymentRecords: order.paymentRecords?.map(record => ({
            id: record.id,
            paymentMethodKey: record.PaymentMethod.key,
            paymentAmount: record.paymentAmount,
            reference: record.reference || '',
            paymentDate: record.paymentDate,
            proofUrl: record.proofUrl || null,
            isVerified: !!record.isVerified,
          })) || []
        };
        
        setFormData(transformedData);
        setInitialFormData(JSON.parse(JSON.stringify(transformedData)));
        
      } catch (err) {
        console.error('Failed to load order:', err);
        error(t('ecomOrders.failedToLoadOrder'), { title: t('common.error'), duration: 6000 });
        navigate('/orders');
      } finally {
        setIsLoadingOrder(false);
      }
    };

    if (orderId && !isLoadingReference) {
      loadOrderData();
    }
  }, [orderId, isLoadingReference, navigate, error, t, setFormData, user, setReplacedOrderData]);

  const getChangedFields = () => {
    if (!initialFormData) return formData;
    const changes = {};
    const scalarFields = [
      'customerName', 'privateReference', 'address', 'cityId',
      'customerEmail', 'deliveryNotes', 'specialNotes',
      'orderStatusKey', 'orderStatusReasonKey', 'holdUntilDate',
      'paymentTypeKey', 'replacedOrderId', 'replacementReasonKey',
      'replacementCodAmount', 'returnAirwaybillNumber', 'deliveryCharge', 'weight',
      'orderOriginKey', 'messageCenterShiftId'
    ];
    scalarFields.forEach(field => {
      if (formData[field] !== initialFormData[field]) changes[field] = formData[field];
    });
    if (JSON.stringify(formData.phoneNumbers) !== JSON.stringify(initialFormData.phoneNumbers)) {
      changes.phoneNumbers = formData.phoneNumbers;
    }
    if (JSON.stringify(formData.orderItems) !== JSON.stringify(initialFormData.orderItems)) {
      changes.orderItems = formData.orderItems.map(item => ({
        id: typeof item.id === 'number' && item.id < 1e12 ? item.id : undefined,
        productId: item.productId,
        variationId: item.variationId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        lineDiscount: item.lineDiscount || 0,
        ...(item.bundleGroupKey && {
          bundleProductId: item.bundleProductId,
          bundleGroupKey: item.bundleGroupKey,
          bundleQuantity: item.bundleQuantity,
        }),
        ...(crossSellingSettings.enabled && item.isCrossSell && {
          isCrossSell: true,
          crossSellQuantity: item.crossSellQuantity || item.quantity,
        }),
      }));
    }
    const initialReturnQtys = (initialFormData.returnItems || []).map(i => ({ replacedOrderItemId: i.replacedOrderItemId, returnQty: i.returnQty }));
    const currentReturnQtys = (formData.returnItems || []).map(i => ({ replacedOrderItemId: i.replacedOrderItemId, returnQty: i.returnQty }));
    if (JSON.stringify(currentReturnQtys) !== JSON.stringify(initialReturnQtys)) {
      changes.returnItems = (formData.returnItems || [])
        .filter(item => item.returnQty > 0)
        .map(item => ({
          replacedOrderItemId: item.replacedOrderItemId,
          returnQuantity: item.returnQty,
        }));
    }
    if (JSON.stringify(formData.paymentRecords) !== JSON.stringify(initialFormData.paymentRecords)) {
      changes.paymentRecords = formData.paymentRecords.map(record => ({
        id: typeof record.id === 'number' && record.id < 1e12 ? record.id : undefined,
        paymentMethodKey: record.paymentMethodKey,
        paymentAmount: parseFloat(record.paymentAmount),
        reference: record.reference || '',
        paymentDate: record.paymentDate,
        proofUrl: record.proofUrl || null,
      }));
    }
    return changes;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) {
      error(t('ecomOrders.someFieldsRequired'), { title: t('common.validationError'), duration: 5000 });
      return;
    }

    setIsSubmitting(true);

    try {
      const changedFields = getChangedFields();
      if (Object.keys(changedFields).length === 0) {
        error('No changes detected', { title: t('common.info'), duration: 3000 });
        setIsSubmitting(false);
        return;
      }
      Object.keys(changedFields).forEach(key => {
        if (typeof changedFields[key] === 'string') changedFields[key] = changedFields[key].trim();
        if (changedFields[key] === '' || changedFields[key] === null) {
          if (!['customerName', 'address', 'cityId', 'phoneNumbers', 'orderStatusKey', 'paymentTypeKey', 'orderItems', 'orderOriginKey', 'messageCenterShiftId', 'privateReference', 'customerEmail', 'deliveryNotes', 'specialNotes'].includes(key)) {
            delete changedFields[key];
          }
        }
      });
      await updateEcomOrder(orderId, changedFields);
      success(t('ecomOrders.orderUpdatedSuccess'), { title: t('common.success'), duration: 4000 });
      navigate('/orders');
    } catch (err) {
      console.error('Order update error:', err);
      const errorMessage = err.response?.data?.message || t('ecomOrders.failedToUpdateOrder');
      error(errorMessage, { title: t('common.error'), duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatGrandTotal = (amount) => {
    if (!amount) return null;
    return `Rs. ${parseFloat(amount).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Derived state for conditional action bar fields
  const selectedStatus = referenceData.orderStatuses?.find(s => s.key === formData.orderStatusKey);
  const resolvedStatusReasons = getStatusReasons();

  if (isLoadingReference) {
    return (
      <div className={styles.createOrderPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <ShoppingCart className={styles.headerIcon} size={22} />
              <div className={styles.titleContent}><h1>{t('ecomOrders.editOrder')}</h1></div>
            </div>
          </div>
        </div>
        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('ecomOrders.loadingReferenceData')}</h3>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingOrder) {
    return (
      <div className={styles.createOrderPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <ShoppingCart className={styles.headerIcon} size={22} />
              <div className={styles.titleContent}><h1>{t('ecomOrders.editOrder')}</h1></div>
            </div>
          </div>
        </div>
        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <Loader2 size={36} className={styles.loadingSpinnerLarge} />
            <h3>{t('ecomOrders.loadingOrder')}</h3>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) return null;

  return (
    <div className={styles.createOrderPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <ShoppingCart className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('ecomOrders.editOrder')}</h1>
              <p>Order #{orderData.orderNumber}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            {hasPermission('create_ecom_order') && (
              <button onClick={() => navigate('/orders/create')} className={styles.newOrderButton}>
                <Plus size={16} />
                {t('ecomOrders.addOrder')}
              </button>
            )}
            <button onClick={() => navigate('/orders')} className={styles.backButton}>
              <ArrowLeft size={16} />
              {t('ecomOrders.backToOrders')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        <form onSubmit={handleSubmit} className={styles.orderForm}>
          {/* TOP 2-COLUMN: Shop+Customer | Items+Summary */}
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <Store className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.shopSelection')}</h2>
                </div>
                <ShopSelectionSection
                  selectedShop={selectedShop}
                  userShops={userShops}
                  onShopChange={() => {}}
                  orderNumberPreview={orderData.orderNumber}
                  error={errors.shopId}
                  isReadOnly={true}
                  compact={true}
                />
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <User className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.customerInformation')}</h2>
                </div>
                <CustomerInformationSection
                  formData={formData}
                  requiredFields={requiredFields}
                  cities={referenceData.cities}
                  errors={errors}
                  isCalculatingDelivery={isCalculatingDelivery}
                  onFieldChange={handleFieldChange}
                  onPhoneNumberChange={handlePhoneNumberChange}
                  onPhoneValidationChange={handlePhoneValidationChange}
                  onTogglePrimaryPhone={togglePrimaryPhone}
                  onAddPhoneNumber={addPhoneNumber}
                  onRemovePhoneNumber={removePhoneNumber}
                />
              </div>

              <div className={`${styles.formSection} ${errors.orderOriginKey ? styles.formSectionError : ''}`}>
                <div className={styles.sectionHeader}>
                  <Store className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.orderOrigin') || 'Order Origin'}{requiredFields.orderOriginKey && ' *'}</h2>
                </div>
                <OrderOriginSection
                  value={formData.orderOriginKey}
                  onChange={(key) => handleFieldChange('orderOriginKey', key)}
                  error={errors.orderOriginKey}
                />
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <Package className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.orderItems')}</h2>
                </div>
                <OrderItemsSection
                  shopId={formData.shopId}
                  orderItems={formData.orderItems}
                  errors={errors}
                  canChangeUnitPrice={canChangeUnitPrice}
                  canGiveDiscount={canGiveDiscount}
                  crossSellEnabled={!!crossSellingSettings.enabled}
                  currentUserId={user?.id}
                  createdByUserId={formData.createdByUserId}
                  onAddItem={addOrderItem}
                  onUpdateItem={updateOrderItem}
                  onRemoveItem={removeOrderItem}
                  onUpdateBundleQuantity={updateBundleQuantity}
                  onRemoveBundle={removeBundle}
                  onSetBundleCrossSell={setBundleCrossSell}
                  allowDiscountOverLine={isReplacementOrder()}
                />
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <FileText className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.orderSummary')}</h2>
                </div>
                <OrderSummarySection
                  orderTotals={orderTotals}
                  paymentRecords={formData.paymentRecords}
                  isCalculatingDelivery={isCalculatingDelivery}
                  canChangeDeliveryCharge={canChangeDeliveryCharge}
                  deliveryCharge={formData.deliveryCharge}
                  onDeliveryChargeChange={(value) => handleFieldChange('deliveryCharge', value)}
                  weight={formData.weight}
                  onWeightChange={handleWeightChange}
                  hidePaymentSummary={true}
                  deliveryChargeError={errors.deliveryCharge}
                />
              </div>
            </div>
          </div>

          {/* BOTTOM 2-COLUMN: Status | Payment Records */}
          <div className={styles.bottomColumns}>
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <CheckCircle className={styles.sectionIcon} size={16} />
                <h2>{t('ecomOrders.orderStatusAndType')}</h2>
              </div>
              <OrderStatusSection
                formData={formData}
                referenceData={referenceData}
                selectableOrderStatuses={selectableOrderStatuses}
                errors={errors}
                onFieldChange={handleFieldChange}
                getStatusReasons={getStatusReasons}
                isReplacementOrder={isReplacementOrder()}
                replacedOrderData={replacedOrderData}
                isLookingUpOrder={isLookingUpOrder}
                orderLookupError={orderLookupError}
                isEditMode={true}
                orderTotals={orderTotals}
                onUpdateReturnItemQty={updateReturnItemQty}
                returnPackageOrder={orderData.replacementDetail?.returnPackageOrder || null}
              />
            </div>

            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <CreditCard className={styles.sectionIcon} size={16} />
                <h2>{t('ecomOrders.paymentRecords')}</h2>
              </div>
              {(requiresPaymentRecords() || allowsOptionalPaymentRecords()) ? (
                <PaymentRecordsSection
                  paymentRecords={formData.paymentRecords}
                  paymentMethods={referenceData.paymentMethods}
                  orderTotal={orderTotals.grandTotal}
                  errors={errors}
                  required={requiresPaymentRecords()}
                  onAddRecord={addPaymentRecord}
                  onUpdateRecord={updatePaymentRecord}
                  onRemoveRecord={removePaymentRecord}
                />
              ) : (
                <div className={styles.paymentNotRequired}>
                  <CreditCard size={28} className={styles.paymentNotRequiredIcon} />
                  <p>{t('ecomOrders.paymentRecordsNotRequired') || 'No payment records required'}</p>
                  <span>{t('ecomOrders.paymentRecordsNotRequiredHint') || 'Select a payment type that requires records to add payment details.'}</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.actionBarSpacer} />
        </form>
      </div>

      {/* FIXED BOTTOM ACTION BAR */}
      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>

          {/* ── Compact Status Controls (desktop only) ── */}
          <div className={styles.actionBarStatus}>
            <span className={styles.actionBarStatusLabel}>Status</span>

            {/* Order Status select */}
            <div className={styles.actionBarStatusDropdown} title={t('ecomOrders.orderStatus')}>
              <Dropdown
                value={formData.orderStatusKey}
                onChange={(value) => value && handleFieldChange('orderStatusKey', value)}
                options={selectableOrderStatuses.map((status) => ({ value: status.key, label: status.label }))}
                icon={<CheckCircle size={13} />}
                allowClear={false}
                size="compact"
                dropdownPosition="top"
              />
            </div>

            {/* Status Reason — shown only when selected status requiresReason */}
            {selectedStatus?.requiresReason && (
              <div className={styles.actionBarStatusDropdown} title={t('ecomOrders.statusReason')}>
                <Dropdown
                  value={formData.orderStatusReasonKey || ''}
                  onChange={(value) => handleFieldChange('orderStatusReasonKey', value || '')}
                  options={resolvedStatusReasons.map(reason => ({ value: reason.key, label: reason.label }))}
                  placeholder={t('ecomOrders.selectStatusReason')}
                  icon={<CheckCircle size={13} />}
                  size="compact"
                  dropdownPosition="top"
                />
              </div>
            )}

            {/* Hold Until Date — shown only when selected status requiresHoldDate */}
            {selectedStatus?.requiresHoldDate && (
              <div className={styles.actionBarStatusDropdown} title={t('ecomOrders.holdUntilDate')}>
                <DatePicker
                  value={formData.holdUntilDate || ''}
                  onChange={(value) => handleFieldChange('holdUntilDate', value || '')}
                  minDate={new Date().toISOString().split('T')[0]}
                  placeholder={t('ecomOrders.holdUntilDate')}
                  size="compact"
                  dropdownPosition="top"
                />
              </div>
            )}
          </div>

          {/* ── Message Center Shift picker (gated by business setting + shop eligibility) ── */}
          {messageCenterSettings.enabled && selectedShop?.messageCenterShiftEligible && (
            <ShiftPickerSection
              value={formData.messageCenterShiftId}
              onChange={(shiftId) => handleFieldChange('messageCenterShiftId', shiftId)}
              disabled={formData.createdByUserId != null && formData.createdByUserId !== user?.id}
              initialDate={formData.messageCenterShiftDate}
              autoSelectActiveShift={false}
            />
          )}

          {/* ── Meta: total + item count ── */}
          <div className={styles.actionBarMeta}>
            {orderTotals.grandTotal > 0 && (
              <span className={styles.actionBarTotal}>
                Total: <strong>{formatGrandTotal(orderTotals.grandTotal)}</strong>
              </span>
            )}
            {formData.orderItems.length > 0 && (
              <span className={styles.actionBarItemCount}>
                {formData.orderItems.length} item{formData.orderItems.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* ── Action buttons ── */}
          <div className={styles.actionBarButtons}>
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className={styles.cancelButton}
              disabled={isSubmitting}
            >
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={styles.submitButton}
              disabled={isSubmitting || isLoadingOrder}
            >
              {isSubmitting ? (
                <div className={styles.loadingSpinner} />
              ) : (
                <>
                  <Save size={15} />
                  {t('ecomOrders.updateOrder')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrderPage;